import { CONTACT_SECTIONS } from '#app/routes/mocra+/_form+/adverse-events/contact-sections.ts'
import {
	type AdverseEventContactEntry,
	type AdverseEventsFormValues,
} from '#app/routes/mocra+/_form+/adverse-events/form.ts'
import { type CompanyFormValues } from '#app/routes/mocra+/_form+/company.tsx'
import {
	COUNTRY_OPTIONS,
	US_STATE_OPTIONS,
} from '#app/routes/mocra+/_form+/components/addresses/schema.ts'
import {
	sanitizeDistributionSystemDraft,
	isDistributionSystemValue,
} from '#app/routes/mocra+/_form+/components/distribution-systems/types.ts'
import {
	FACILITY_TYPE_OPTIONS,
	FEI_STATUS_PER_FACILITY,
	REGISTRATION_STATUS_OPTIONS,
} from '#app/routes/mocra+/_form+/components/facilities/schema.ts'
import { REPOSITORY_SYSTEM_OPTIONS } from '#app/routes/mocra+/_form+/components/repositories/constants.ts'
import {
	isSalesChannelTypeValue,
	sanitizeSalesChannelDraft,
} from '#app/routes/mocra+/_form+/components/sales-channels/types.ts'
import { SUPPLIER_RISK_LEVEL_OPTIONS } from '#app/routes/mocra+/_form+/components/suppliers/constants.ts'
import {
	ROUTINE_TEST_NAME_OPTIONS,
	TEST_FREQUENCY_OPTIONS,
} from '#app/routes/mocra+/_form+/components/test-routines/constants.ts'
import { type FacilitiesFormValues } from '#app/routes/mocra+/_form+/facilities.tsx'
import { type IntakeDraft } from '#app/routes/mocra+/_form+/generate/intake-loader.ts'
import {
	ACKNOWLEDGEMENT_CHANNEL_OPTIONS,
	ACKNOWLEDGEMENT_WINDOW_OPTIONS,
	COMPLAINT_CHANNEL_OPTIONS,
	COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS,
	COMPLAINT_SYSTEM_PLATFORM_OPTIONS,
	COMPLAINT_SYSTEM_USAGE_OPTIONS,
	CHANNEL_SYSTEM_FALLBACK_OPTIONS,
	DOCUMENT_LIBRARY_SYSTEM_OPTIONS,
	DOCUMENT_SCOPE_OPTIONS,
	DRILL_CADENCE_OPTIONS,
	IMPLEMENTATION_TRACKING_OPTIONS,
	IMPLEMENTATION_WINDOW_OPTIONS,
	PRODUCT_CATEGORY_OPTIONS,
	PRODUCT_DATA_SOURCE_OPTIONS,
	RECALL_DISTRIBUTION_SYSTEM_OPTIONS,
	RECALL_EVENT_OUTCOME_OPTIONS,
	RECALL_EVENT_TYPE_OPTIONS,
	RECALL_SALES_CHANNEL_OPTIONS,
	REGULATORY_FEED_FREQUENCY_OPTIONS,
	REGULATORY_FEED_OPTIONS,
	REGULATORY_REVIEW_CADENCE_OPTIONS,
	TRAINING_AUDIENCE_OPTIONS,
	TRAINING_DELIVERY_OPTIONS,
	TRAINING_FREQUENCY_OPTIONS,
	TRAINING_RECORD_RETENTION_OPTIONS,
	POLICY_REFERENCE_ITEMS,
	POLICY_REFERENCE_STATUS_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'
import { type PoliciesReferencesFormValues } from '#app/routes/mocra+/_form+/policies-references.tsx'
import { type PrimaryContactsFormValues } from '#app/routes/mocra+/_form+/primary-contacts.tsx'
import { type ProductsFormValues } from '#app/routes/mocra+/_form+/products.tsx'
import { type RecallPreparednessFormValues } from '#app/routes/mocra+/_form+/recall-preparedness.tsx'
import { type RecordsAccessFormValues } from '#app/routes/mocra+/_form+/records-access.tsx'
import { type RegulatoryMonitoringFormValues } from '#app/routes/mocra+/_form+/regulatory-monitoring.tsx'
import { type SuppliersTestingFormValues } from '#app/routes/mocra+/_form+/suppliers-testing.tsx'
import { type TrainingDocumentationFormValues } from '#app/routes/mocra+/_form+/training-documentation.tsx'
import regulatoryWatchTemplate from '../../../../../mocra/sop-templates/§605-612-regulatory-watch.md?raw'
import supplierOversightTemplate from '../../../../../mocra/sop-templates/§605-612-supplier-oversight.md?raw'
import trainingDocumentationTemplate from '../../../../../mocra/sop-templates/§605-612-training-documentation.md?raw'
import adverseEventTemplate from '../../../../../mocra/sop-templates/§605-adverse-event.md?raw'
import gmpPrepTemplate from '../../../../../mocra/sop-templates/§606-gmp-prep.md?raw'
import facilityRegistrationTemplate from '../../../../../mocra/sop-templates/§607-facility-registration.md?raw'
import productListingTemplate from '../../../../../mocra/sop-templates/§607-product-listing.md?raw'
import safetySubstantiationTemplate from '../../../../../mocra/sop-templates/§608-safety-substantiation.md?raw'
import labelGovernanceTemplate from '../../../../../mocra/sop-templates/§609-label-governance.md?raw'
import recordsAccessTemplate from '../../../../../mocra/sop-templates/§610-records-access.md?raw'
import recallManagementTemplate from '../../../../../mocra/sop-templates/§611-recall-management.md?raw'

export type TemplateId =
	| 'adverse-event'
	| 'gmp-prep'
	| 'facility-registration'
	| 'product-listing'
	| 'safety-substantiation'
	| 'label-governance'
	| 'records-access'
	| 'recall-management'
	| 'training-documentation'
	| 'supplier-oversight'
	| 'regulatory-watch'

type TemplateHelpers = {
	present: (value?: string | null) => string
	optionLabel: (
		options: readonly { value: string; label: string }[],
		value: string | undefined,
	) => string | null
	buildSummary: (
		items: Array<{ label: string; value: string | null | undefined }>,
	) => string[]
	contactLine: (name?: string, email?: string, phone?: string) => string | null
}

type TemplateDefinition = {
	id: TemplateId
	title: string
	filename: string
	markdown: string
	summary: (draft: IntakeDraft, helpers: TemplateHelpers) => string[]
}

export type RenderedTemplate = TemplateDefinition & {
	content: string
	summaryLines: string[]
}

const TEMPLATES: readonly TemplateDefinition[] = [
	{
		id: 'adverse-event',
		title: '§605 Serious Adverse Event Intake & Reporting',
		filename: '§605-adverse-event.md',
		markdown: adverseEventTemplate,
		summary: (draft, helpers) => {
			const regulatoryContact = resolvePrimaryContact(
				draft.primaryContacts,
				'regulatoryLeadId',
			)
			const regulatoryLead = regulatoryContact
				? helpers.contactLine(
						regulatoryContact.name,
						regulatoryContact.email,
						regulatoryContact.phone,
					)
				: null
			return helpers.buildSummary([
				{
					label: 'Responsible party label',
					value: draft.company?.responsiblePersonLabelName,
				},
				{ label: 'Brands covered', value: formatBrands(draft.company) },
				{
					label: 'Complaint intake channels',
					value: summarizeComplaintChannels(draft.adverseEvents),
				},
				{
					label: 'Complaint systems',
					value: summarizeComplaintSystems(draft.adverseEvents),
				},
				{
					label: 'Acknowledgement service level',
					value: summarizeAcknowledgement(draft.adverseEvents),
				},
				{
					label: 'Medical reviewers',
					value: summarizeMedicalReviewers(draft.adverseEvents),
				},
				{
					label: 'Escalation ladder',
					value: summarizeAdverseEventContacts(draft.adverseEvents),
				},
				{ label: 'Regulatory lead', value: regulatoryLead },
				{
					label: 'Record custodians',
					value: summarizeRecordCustodians(draft.recordsAccess),
				},
			])
		},
	},
	{
		id: 'gmp-prep',
		title: '§606 Good Manufacturing Practice Preparation',
		filename: '§606-gmp-prep.md',
		markdown: gmpPrepTemplate,
		summary: (draft, helpers) =>
			helpers.buildSummary([
				{
					label: 'Facility roster',
					value: summarizeFacilityRoster(draft.facilities),
				},
				{
					label: 'Supplier roster',
					value: summarizeSuppliers(draft.suppliersTesting),
				},
				{
					label: 'Routine product & micro testing',
					value: summarizeRoutineTests(draft.suppliersTesting),
				},
				{
					label: 'Safety dossier location',
					value: summarizeSafetyRepository(draft.suppliersTesting),
				},
				{
					label: 'Document libraries',
					value: summarizeDocumentLibraries(draft.trainingDocumentation),
				},
				{
					label: 'Training programs',
					value: summarizeTrainingPrograms(draft.trainingDocumentation),
				},
				{
					label: 'Training record retention',
					value: summarizeRecordRetention(draft.trainingDocumentation),
				},
				{
					label: 'Sanitation SOP bundle',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'sanitation_sop_bundle',
					),
				},
				{
					label: 'Equipment maintenance SOP',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'equipment_maintenance_sop',
					),
				},
				{
					label: 'Deviation / CAPA procedure',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'deviation_capa_procedure',
					),
				},
			]),
	},
	{
		id: 'facility-registration',
		title: '§607 Facility Registration',
		filename: '§607-facility-registration.md',
		markdown: facilityRegistrationTemplate,
		summary: (draft, helpers) => {
			const facilityRosterSummary = summarizeFacilityRoster(draft.facilities)
			return helpers.buildSummary([
				{
					label: 'Responsible company legal name',
					value: draft.company?.companyLegalName,
				},
				{
					label: 'Headquarters address',
					value: formatAddress(draft.company?.hqAddress),
				},
				{
					label: 'Facility roster',
					value: facilityRosterSummary,
				},
			])
		},
	},
	{
		id: 'product-listing',
		title: '§607 Product Listing',
		filename: '§607-product-listing.md',
		markdown: productListingTemplate,
		summary: (draft, helpers) => {
			const catalogSummary = summarizeProductCatalog(draft.products)
			const professionalSummary = summarizeProfessionalOnlyProducts(
				draft.products,
			)
			const dataSourceSummary = summarizeProductDataSource(draft.products)
			return helpers.buildSummary([
				{
					label: 'Brands or product lines',
					value: formatBrands(draft.company),
				},
				{
					label: 'Active SKU count',
					value: draft.products?.activeSkuCount,
				},
				{
					label: 'Product catalog',
					value: catalogSummary,
				},
				{
					label: 'Professional-only products',
					value: professionalSummary,
				},
				{
					label: 'Authoritative product data source',
					value: dataSourceSummary,
				},
			])
		},
	},
	{
		id: 'safety-substantiation',
		title: '§608 Safety Substantiation',
		filename: '§608-safety-substantiation.md',
		markdown: safetySubstantiationTemplate,
		summary: (draft, helpers) => {
			const catalogSummary = summarizeProductCatalog(draft.products)
			const professionalSummary = summarizeProfessionalOnlyProducts(
				draft.products,
			)
			return helpers.buildSummary([
				{
					label: 'Supplier roster',
					value: summarizeSuppliers(draft.suppliersTesting),
				},
				{
					label: 'Safety dossier location',
					value: summarizeSafetyRepository(draft.suppliersTesting),
				},
				{
					label: 'Routine testing program',
					value: summarizeRoutineTests(draft.suppliersTesting),
				},
				{
					label: 'Product catalog',
					value: catalogSummary,
				},
				{
					label: 'Professional-only products',
					value: professionalSummary,
				},
				{
					label: 'Risk management policy',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'risk_management_policy',
					),
				},
				{
					label: 'Risk management program / SOP',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'risk_management_program',
					),
				},
			])
		},
	},
	{
		id: 'label-governance',
		title: '§609 Label Governance',
		filename: '§609-label-governance.md',
		markdown: labelGovernanceTemplate,
		summary: (draft, helpers) =>
			helpers.buildSummary([
				{
					label: 'Consumer contact line on labels',
					value: draft.labelGovernance?.consumerContactLine,
				},
				{
					label: 'Channels printed on labels',
					value: summarizeLabelChannels(
						draft.labelGovernance?.labelIntakeChannels,
					),
				},
				{
					label: 'Artwork system of record',
					value: draft.labelGovernance?.artworkSystem,
				},
				{
					label: 'Label approval owner',
					value: draft.labelGovernance?.approvalOwner,
				},
				{
					label: 'Allergen tracking process',
					value: draft.labelGovernance?.allergenTracking,
				},
				{
					label: 'Label change workflow',
					value: draft.labelGovernance?.labelChangeProcess,
				},
				{
					label: 'Artwork approval workflow reference',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'artwork_approval_workflow',
					),
				},
				{
					label: 'Brand guidelines reference',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'brand_guidelines',
					),
				},
			]),
	},
	{
		id: 'records-access',
		title: '§610 Records Access',
		filename: '§610-records-access.md',
		markdown: recordsAccessTemplate,
		summary: (draft, helpers) => {
			const drillCadenceLabel =
				helpers.optionLabel(
					DRILL_CADENCE_OPTIONS,
					draft.recordsAccess?.drillCadence,
				) ?? draft.recordsAccess?.drillCadence
			return helpers.buildSummary([
				{
					label: 'Regulated record repositories',
					value: summarizeRecordRepositories(draft.recordsAccess),
				},
				{
					label: 'Record custodians',
					value: summarizeRecordCustodians(draft.recordsAccess),
				},
				{
					label: 'Secure transfer method',
					value: summarizeSecureTransfer(draft.recordsAccess),
				},
				{ label: 'Drill cadence', value: drillCadenceLabel },
				{
					label: 'Distribution data source',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'distribution_records_dataset',
					),
				},
				{
					label: 'Headquarters address',
					value: formatAddress(draft.company?.hqAddress),
				},
			])
		},
	},
	{
		id: 'recall-management',
		title: '§611 Recall Management',
		filename: '§611-recall-management.md',
		markdown: recallManagementTemplate,
		summary: (draft, helpers) => {
			const operationsContact = resolvePrimaryContact(
				draft.primaryContacts,
				'operationsLeadId',
			)
			const operationsLead = operationsContact
				? helpers.contactLine(
						operationsContact.name,
						operationsContact.email,
						operationsContact.phone,
					)
				: null
			return helpers.buildSummary([
				{
					label: 'Sales channels impacted',
					value: summarizeSalesChannels(draft.recallPreparedness),
				},
				{
					label: 'Distribution & lot tracking systems',
					value: summarizeDistributionSystems(draft.recallPreparedness),
				},
				{
					label: 'Incident response team',
					value: summarizeIncidentTeam(draft.recallPreparedness),
				},
				{
					label: 'Recall / withdrawal history',
					value: summarizeRecallHistory(draft.recallPreparedness),
				},
				{
					label: 'Supplier roster',
					value: summarizeSuppliers(draft.suppliersTesting),
				},
				{ label: 'Operations lead', value: operationsLead },
			])
		},
	},
	{
		id: 'training-documentation',
		title: '§605–§612 Training & Documentation Control',
		filename: '§605-612-training-documentation.md',
		markdown: trainingDocumentationTemplate,
		summary: (draft, helpers) => {
			return helpers.buildSummary([
				{
					label: 'Document libraries',
					value: summarizeDocumentLibraries(draft.trainingDocumentation),
				},
				{
					label: 'Training programs',
					value: summarizeTrainingPrograms(draft.trainingDocumentation),
				},
				{
					label: 'Training record retention',
					value: summarizeRecordRetention(draft.trainingDocumentation),
				},
				{
					label: 'HR training policy reference',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'hr_training_policy',
					),
				},
				{
					label: 'Change control policy reference',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'change_control_policy',
					),
				},
				{
					label: 'Document control policy reference',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'document_control_policy',
					),
				},
			])
		},
	},
	{
		id: 'supplier-oversight',
		title: '§605–§612 Supplier Oversight',
		filename: '§605-612-supplier-oversight.md',
		markdown: supplierOversightTemplate,
		summary: (draft, helpers) =>
			helpers.buildSummary([
				{
					label: 'Supplier roster',
					value: summarizeSuppliers(draft.suppliersTesting),
				},
				{
					label: 'Routine testing program',
					value: summarizeRoutineTests(draft.suppliersTesting),
				},
				{
					label: 'Safety dossier location',
					value: summarizeSafetyRepository(draft.suppliersTesting),
				},
				{
					label: 'Quality agreement template',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'quality_agreement_template',
					),
				},
				{
					label: 'Supplier qualification policy',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'supplier_qualification_policy',
					),
				},
			]),
	},
	{
		id: 'regulatory-watch',
		title: '§605–§612 Regulatory Monitoring & Change Control',
		filename: '§605-612-regulatory-watch.md',
		markdown: regulatoryWatchTemplate,
		summary: (draft, helpers) =>
			helpers.buildSummary([
				{
					label: 'Monitoring owners',
					value: summarizeMonitoringOwners(draft.regulatoryMonitoring),
				},
				{
					label: 'Monitoring tools & feeds',
					value: summarizeMonitoringFeeds(draft.regulatoryMonitoring),
				},
				{
					label: 'Change control board',
					value: summarizeChangeControlApprovers(draft.regulatoryMonitoring),
				},
				{
					label: 'Implementation window',
					value: summarizeImplementationWindow(draft.regulatoryMonitoring),
				},
				{
					label: 'Review cadence',
					value: summarizeReviewCadence(draft.regulatoryMonitoring),
				},
				{
					label: 'Tracking method',
					value: summarizeTrackingTool(draft.regulatoryMonitoring),
				},
				{
					label: 'Risk management policy reference',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'risk_management_policy',
					),
				},
				{
					label: 'Change control policy reference',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'change_control_policy',
					),
				},
				{
					label: 'Change control procedure reference',
					value: policyDocumentSummary(
						draft.policiesReferences,
						'change_control_procedure',
					),
				},
			]),
	},
] as const

function normalizeValue(value?: string | null) {
	if (!value) return null
	const trimmed = value.trim()
	if (!trimmed) return null
	return trimmed
		.replace(/\r?\n+/g, '; ')
		.replace(/\s{2,}/g, ' ')
		.trim()
}

function present(value?: string | null) {
	return normalizeValue(value) ?? 'Not provided yet'
}

function optionLabel(
	options: readonly { value: string; label: string }[],
	value: string | undefined,
) {
	if (!value) return null
	const match = options.find((option) => option.value === value)
	return match?.label ?? value
}

function summarizeFacilityRoster(facilities?: FacilitiesFormValues) {
	const entries = facilities?.facilities
	if (!entries?.length) return null
	return entries
		.map((facility) => {
			const typeLabel =
				optionLabel(FACILITY_TYPE_OPTIONS, facility.type) ?? facility.type
			const registrationLabel =
				optionLabel(REGISTRATION_STATUS_OPTIONS, facility.registrationStatus) ??
				facility.registrationStatus
			const feiStatusLabel =
				optionLabel(FEI_STATUS_PER_FACILITY, facility.feiStatus) ??
				facility.feiStatus
			const feiNumber =
				facility.feiStatus === 'present' && facility.feiNumber
					? `FEI ${facility.feiNumber}`
					: null
			const header = [facility.name, typeLabel].filter(Boolean).join(' — ')
			const details = [
				formatAddress(facility.address),
				registrationLabel ? `Registration: ${registrationLabel}` : null,
				feiStatusLabel
					? `FEI status: ${feiStatusLabel}${feiNumber ? ` (${feiNumber})` : ''}`
					: null,
			]
				.filter(Boolean)
				.join(' | ')
			return [header, details]
				.filter((value) => value && value.length > 0)
				.join(' — ')
		})
		.join('\n')
}

function summarizeLabelChannels(channels?: ReadonlyArray<string>) {
	if (!channels?.length) return null
	const labels = channels
		.map(
			(channel) => optionLabel(COMPLAINT_CHANNEL_OPTIONS, channel) ?? channel,
		)
		.filter((label): label is string => Boolean(label && label.length))
	const unique = Array.from(new Set(labels))
	if (unique.length === 0) return null
	return unique.join(', ')
}

function summarizeSuppliers(data?: SuppliersTestingFormValues) {
	const suppliers = data?.suppliers
	if (!suppliers?.length) return null
	return suppliers
		.map((supplier) => {
			const riskLabel =
				optionLabel(SUPPLIER_RISK_LEVEL_OPTIONS, supplier.riskLevel) ??
				supplier.riskLevel
			const agreement = supplier.qualityAgreementSigned
				? 'QA signed'
				: 'QA pending'
			const contact = supplier.contactEmail?.trim()
				? `Contact: ${supplier.contactEmail.trim()}`
				: null
			return [
				supplier.name,
				`Scope: ${supplier.materialScope}`,
				`Risk: ${riskLabel}`,
				agreement,
				contact,
			]
				.filter(Boolean)
				.join(' | ')
		})
		.join('\n')
}

function summarizeRoutineTests(data?: SuppliersTestingFormValues) {
	const tests = data?.routineTests
	if (!tests?.length) return null
	return tests
		.map((test) => {
			const frequencyLabel =
				optionLabel(TEST_FREQUENCY_OPTIONS, test.frequency) ?? test.frequency
			const nameLabel =
				test.name === 'other'
					? test.nameOther?.trim() || 'Other test'
					: (optionLabel(ROUTINE_TEST_NAME_OPTIONS, test.name) ?? test.name)
			return [nameLabel, `Frequency: ${frequencyLabel}`]
				.filter(Boolean)
				.join(' | ')
		})
		.join('\n')
}

function summarizeSafetyRepository(data?: SuppliersTestingFormValues) {
	const repository = data?.safetyRepository
	if (!repository) return null
	const systemLabel =
		optionLabel(REPOSITORY_SYSTEM_OPTIONS, repository.system) ??
		repository.system
	return [systemLabel, repository.location].filter(Boolean).join(' — ')
}

function summarizeProductCatalog(products?: ProductsFormValues | null) {
	if (!products?.products?.length) return null
	const counts = new Map<string, number>()
	for (const product of products.products) {
		const categoryLabel =
			product.category === 'other'
				? product.categoryOther?.trim() || 'Other'
				: (optionLabel(PRODUCT_CATEGORY_OPTIONS, product.category) ??
					product.category)
		const label = categoryLabel.trim()
		if (!label) continue
		counts.set(label, (counts.get(label) ?? 0) + 1)
	}
	if (!counts.size) return null
	return Array.from(counts.entries())
		.map(([label, total]) => (total > 1 ? `${label} (${total})` : label))
		.join(', ')
}

function summarizeProfessionalOnlyProducts(
	products?: ProductsFormValues | null,
) {
	if (!products?.products?.length) return 'None'
	const names = products.products
		.filter((product) => product.professionalUse === 'professional_only')
		.map((product) => product.name?.trim())
		.filter((name): name is string => Boolean(name))
	if (names.length === 0) return 'None'
	return names.join(', ')
}

function summarizeProductDataSource(products?: ProductsFormValues | null) {
	const selection = products?.productDataSource
	if (!selection) return null
	const label = optionLabel(PRODUCT_DATA_SOURCE_OPTIONS, selection) ?? selection
	const extra = products?.productDataSourceOther?.trim()
	if (selection === 'other') {
		return extra ?? label
	}
	if (selection === 'unspecified') {
		return label
	}
	return extra ? `${label} — ${extra}` : label
}

function summarizeComplaintChannels(data?: AdverseEventsFormValues) {
	const channels = data?.intakeChannels
	if (!channels?.length) return null
	return channels
		.map((entry) => {
			const channelLabel =
				optionLabel(COMPLAINT_CHANNEL_OPTIONS, entry.channel) ?? entry.channel
			const ownerTypeLabel =
				optionLabel(COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS, entry.ownerType) ??
				entry.ownerType
			const ownerName = normalizeValue(entry.ownerName)
			const ownerDetail = ownerName
				? `${ownerTypeLabel} — ${ownerName}`
				: ownerTypeLabel
			const destination = summarizeChannelDestination(entry.system, data)
			return [channelLabel, ownerDetail, destination]
				.filter(Boolean)
				.join(' — ')
		})
		.join('\n')
}

function formatComplaintSystemUsage(
	usage: AdverseEventsFormValues['complaintSystems'][number]['usage'],
) {
	const stringUsage = typeof usage === 'string' ? usage : undefined
	const values = Array.isArray(usage) ? usage : stringUsage ? [stringUsage] : []
	if (values.length === 0) return 'Unspecified usage'
	return values
		.map((value) => optionLabel(COMPLAINT_SYSTEM_USAGE_OPTIONS, value) ?? value)
		.join(', ')
}

function summarizeComplaintSystems(data?: AdverseEventsFormValues) {
	const systems = data?.complaintSystems
	if (!systems?.length) return null
	return systems
		.map((system) => {
			const platformLabel =
				optionLabel(COMPLAINT_SYSTEM_PLATFORM_OPTIONS, system.platform) ??
				system.platform
			const usageLabel = formatComplaintSystemUsage(system.usage)
			const systemName = normalizeValue(system.name) ?? platformLabel
			const fragments = [
				`${systemName} (${usageLabel})`,
				systemName === platformLabel ? null : platformLabel,
			]
				.filter(Boolean)
				.join(' — ')
			return fragments
		})
		.join('\n')
}

function summarizeChannelDestination(
	systemValue: string | undefined,
	data?: AdverseEventsFormValues,
) {
	const normalized = normalizeValue(systemValue)
	if (!normalized) return null
	const fallbackLabel =
		optionLabel(CHANNEL_SYSTEM_FALLBACK_OPTIONS, normalized) ?? null
	if (fallbackLabel) {
		return `Routed to ${fallbackLabel}`
	}
	const systems = data?.complaintSystems ?? []
	const match = systems.find((system) => {
		const systemName = normalizeValue(system.name)
		return systemName
			? systemName.toLowerCase() === normalized.toLowerCase()
			: false
	})
	if (match) {
		const usageLabel = formatComplaintSystemUsage(match.usage)
		return `Routed to ${match.name} (${usageLabel})`
	}
	return `Routed to ${normalized}`
}

function summarizeAcknowledgement(data?: AdverseEventsFormValues) {
	const ack = data?.acknowledgement
	if (!ack) return null
	const customWindow = normalizeValue(ack.customWindow)
	const windowLabel =
		ack.window === 'custom'
			? (customWindow ?? 'Custom acknowledgement window')
			: (optionLabel(ACKNOWLEDGEMENT_WINDOW_OPTIONS, ack.window) ?? ack.window)
	const channelLabel =
		optionLabel(ACKNOWLEDGEMENT_CHANNEL_OPTIONS, ack.channel) ?? ack.channel
	const parts = [`${windowLabel} via ${channelLabel}`]
	if (ack.coversWeekends) {
		parts.push('After-hours coverage in place')
	}
	return parts.join(' — ')
}

function summarizeMedicalReviewers(data?: AdverseEventsFormValues) {
	const reviewers = data?.medicalReviewers
	if (!reviewers?.length) return null
	return reviewers
		.map((reviewer, index) => {
			const role = normalizeValue(reviewer.role) ?? 'Medical reviewer'
			const contact =
				contactLine(
					reviewer.name ?? reviewer.fallbackName,
					reviewer.email ?? reviewer.fallbackEmail,
					reviewer.phone ?? reviewer.fallbackPhone,
				) ?? ''
			const label =
				contact.trim().length > 0
					? contact
					: `Reviewer ${reviewer.contactId ?? index + 1}`
			return [role, label].filter(Boolean).join(' — ')
		})
		.join('\n')
}

function summarizeAdverseEventContacts(data?: AdverseEventsFormValues) {
	if (!data) return null
	const sections = CONTACT_SECTIONS.map((section) => {
		const contacts = data[section.key]
		if (!contacts?.length) return null
		const lines = contacts
			.map((contact: AdverseEventContactEntry) => {
				const contactInfo = contactLine(
					contact.name,
					contact.email,
					contact.phone,
				)
				return [contact.role, contactInfo].filter(Boolean).join(' — ')
			})
			.join('\n')
		return `${section.title}\n${lines}`
	}).filter(Boolean)
	if (sections.length === 0) return null
	return sections.join('\n\n')
}

function summarizeRecordRepositories(records?: RecordsAccessFormValues) {
	const repositories = records?.repositories
	if (!repositories?.length) return null
	return repositories
		.map((repository) => {
			const systemLabel =
				optionLabel(REPOSITORY_SYSTEM_OPTIONS, repository.system) ??
				repository.system
			return [systemLabel, repository.location].filter(Boolean).join(' — ')
		})
		.join('\n')
}

function summarizeRecordCustodians(records?: RecordsAccessFormValues) {
	const custodians = records?.custodians
	if (!custodians?.length) return null
	return custodians
		.map((custodian) => {
			const label = [custodian.fallbackName, custodian.fallbackTitle]
				.filter(Boolean)
				.join(' — ')
			return contactLine(
				label || `Contact ${custodian.contactId}`,
				custodian.fallbackEmail,
				custodian.fallbackPhone,
			)
		})
		.join('\n')
}

const SECURE_TRANSFER_METHOD_LABELS = {
	secure_portal: 'Secure portal / data room',
	encrypted_email: 'Encrypted email package',
	on_site_review: 'On-site review only',
	courier: 'Encrypted media courier',
} as const

function summarizeSecureTransfer(records?: RecordsAccessFormValues) {
	const method = records?.secureTransferMethod
	if (!method) return null
	const label =
		SECURE_TRANSFER_METHOD_LABELS[
			method as keyof typeof SECURE_TRANSFER_METHOD_LABELS
		] ?? method
	return label
}

function summarizeSalesChannels(data?: RecallPreparednessFormValues) {
	const channels = Array.isArray(data?.salesChannels)
		? data?.salesChannels
		: null
	if (!channels || channels.length === 0) return null
	return channels
		.map((channel) => {
			const rawType =
				typeof channel === 'object' &&
				channel !== null &&
				'type' in channel &&
				typeof (channel as { type: unknown }).type === 'string'
					? (channel as { type: string }).type
					: undefined
			const type = isSalesChannelTypeValue(rawType)
				? rawType
				: (RECALL_SALES_CHANNEL_OPTIONS[0]?.value ?? 'direct_to_consumer')
			const name =
				typeof channel === 'object' &&
				channel !== null &&
				'name' in channel &&
				typeof (channel as { name: unknown }).name === 'string'
					? (channel as { name: string }).name
					: ''
			const sanitized = sanitizeSalesChannelDraft({
				type,
				name,
			})
			const typeLabel =
				optionLabel(RECALL_SALES_CHANNEL_OPTIONS, sanitized.type) ??
				sanitized.type
			return `${typeLabel} — ${sanitized.name}`
		})
		.join('\n')
}

function summarizeDistributionSystems(data?: RecallPreparednessFormValues) {
	const systems = Array.isArray(data?.distributionSystems)
		? data?.distributionSystems
		: null
	if (!systems || systems.length === 0) return null
	return systems
		.map((system) => {
			const rawSystem =
				typeof system === 'object' &&
				system !== null &&
				'system' in system &&
				typeof (system as { system: unknown }).system === 'string'
					? (system as { system: string }).system
					: undefined
			const systemValue = isDistributionSystemValue(rawSystem)
				? rawSystem
				: (RECALL_DISTRIBUTION_SYSTEM_OPTIONS[0]?.value ?? 'erp')
			const dataset =
				typeof system === 'object' &&
				system !== null &&
				'dataset' in system &&
				typeof (system as { dataset: unknown }).dataset === 'string'
					? (system as { dataset: string }).dataset
					: ''
			const sanitized = sanitizeDistributionSystemDraft({
				system: systemValue,
				dataset,
			})
			const systemLabel =
				optionLabel(RECALL_DISTRIBUTION_SYSTEM_OPTIONS, sanitized.system) ??
				sanitized.system
			return `${systemLabel} — ${sanitized.dataset}`
		})
		.join('\n')
}

const INCIDENT_TEAM_SUMMARY: ReadonlyArray<{
	readonly key: keyof RecallPreparednessFormValues['incidentTeam']
	readonly label: string
}> = [
	{ key: 'activation', label: 'Activation lead' },
	{ key: 'regulatory', label: 'Regulatory filings & FDA liaison' },
	{ key: 'supplyChain', label: 'Supply chain & lot tracing' },
	{ key: 'manufacturing', label: 'Manufacturing & quality remediation' },
	{ key: 'communications', label: 'Communications & messaging' },
	{ key: 'customerCare', label: 'Customer support & logistics' },
	{ key: 'other', label: 'Additional roles' },
]

const RECALL_HISTORY_STATUS_LABELS = {
	mock_only: 'Mock drills only',
	market_withdrawal: 'Market withdrawal(s)',
	recall: 'Recall(s) filed with FDA',
} as const

function isRecallHistoryStatusValue(
	value: unknown,
): value is keyof typeof RECALL_HISTORY_STATUS_LABELS {
	return typeof value === 'string' && value in RECALL_HISTORY_STATUS_LABELS
}

function summarizeIncidentTeam(data?: RecallPreparednessFormValues) {
	const team = data?.incidentTeam
	if (!team) return null
	const primaryCoordinatorValue = data?.primaryCoordinator
	const primaryCoordinator =
		primaryCoordinatorValue &&
		typeof primaryCoordinatorValue === 'object' &&
		primaryCoordinatorValue !== null &&
		typeof (primaryCoordinatorValue as { contactId?: unknown }).contactId ===
			'string'
			? (primaryCoordinatorValue as {
					contactId: string
					fallbackName?: string
					fallbackEmail?: string
					fallbackPhone?: string
				})
			: null
	const primaryId = primaryCoordinator?.contactId ?? ''
	const primaryLine = primaryCoordinator
		? contactReferenceLine(primaryCoordinator, 'Primary recall coordinator')
		: null
	let primaryCapturedInStages = false
	const lines: string[] = []
	for (const stage of INCIDENT_TEAM_SUMMARY) {
		const contacts = team[stage.key]
		if (!Array.isArray(contacts) || contacts.length === 0) continue
		const contactLines = contacts
			.map((contact) => {
				const line =
					contactReferenceLine(contact, `${stage.key}-${contact.contactId}`) ??
					contact.fallbackName ??
					contact.contactId
				if (primaryId && contact.contactId === primaryId) {
					primaryCapturedInStages = true
					return `${line} (Primary coordinator)`
				}
				return line
			})
			.filter(Boolean)
		if (contactLines.length === 0) continue
		lines.push(`${stage.label} — ${contactLines.join(', ')}`)
	}
	if (primaryLine && !primaryCapturedInStages) {
		lines.unshift(`Primary recall coordinator — ${primaryLine}`)
	}
	return lines.length > 0 ? lines.join('\n') : null
}

function summarizeRecallHistory(data?: RecallPreparednessFormValues) {
	const history = data?.recallHistory
	if (!history || typeof history !== 'object') return null
	const status = (history as { status?: string }).status
	if (status === 'none') {
		return 'No recalls or market withdrawals to date.'
	}
	const events = Array.isArray((history as { events?: unknown }).events)
		? (history as { events: Array<Record<string, unknown>> }).events
		: null
	if (!events || events.length === 0) return null
	const statusLabel = isRecallHistoryStatusValue(status)
		? RECALL_HISTORY_STATUS_LABELS[status]
		: typeof status === 'string'
			? status
			: 'History'
	const lines = events
		.map((event) => {
			const year = typeof event?.year === 'string' ? event.year : 'Year N/A'
			const typeValue = typeof event?.type === 'string' ? event.type : undefined
			const outcomeValue =
				typeof event?.outcome === 'string' ? event.outcome : undefined
			const typeLabel =
				optionLabel(RECALL_EVENT_TYPE_OPTIONS, typeValue ?? '') ??
				typeValue ??
				'Type N/A'
			const outcomeLabel =
				optionLabel(RECALL_EVENT_OUTCOME_OPTIONS, outcomeValue ?? '') ??
				outcomeValue ??
				'Outcome N/A'
			const product =
				typeof event?.product === 'string' ? event.product : 'Product N/A'
			return `${year} — ${typeLabel}: ${product} (${outcomeLabel})`
		})
		.join('\n')
	return [statusLabel, lines].filter(Boolean).join('\n')
}

function summarizeDocumentLibraries(data?: TrainingDocumentationFormValues) {
	const libraries = Array.isArray(data?.documentLibraries)
		? data?.documentLibraries
		: null
	if (!libraries || libraries.length === 0) return null
	return libraries
		.map((library) => {
			const systemLabel =
				optionLabel(
					DOCUMENT_LIBRARY_SYSTEM_OPTIONS,
					typeof library?.system === 'string' ? library.system : '',
				) ?? library?.system
			const scopeLabel =
				optionLabel(
					DOCUMENT_SCOPE_OPTIONS,
					typeof library?.scope === 'string' ? library.scope : '',
				) ?? library?.scope
			return [
				typeof scopeLabel === 'string' ? scopeLabel : null,
				typeof library?.location === 'string' ? library.location : null,
				typeof systemLabel === 'string' ? systemLabel : null,
			]
				.filter(Boolean)
				.join(' — ')
		})
		.join('\n')
}

function summarizeTrainingPrograms(data?: TrainingDocumentationFormValues) {
	const programs = data?.trainingPrograms
	if (!programs?.length) return null
	return programs
		.map((program) => {
			const audienceLabel =
				optionLabel(TRAINING_AUDIENCE_OPTIONS, program.audience) ??
				program.audience
			const deliveryLabel =
				optionLabel(TRAINING_DELIVERY_OPTIONS, program.delivery) ??
				program.delivery
			const cadenceLabel =
				optionLabel(TRAINING_FREQUENCY_OPTIONS, program.cadence) ??
				program.cadence
			const completion = program.tracksCompletion
				? 'Completion tracked'
				: 'Manual acknowledgement'
			return [
				audienceLabel,
				`${program.platform} (${deliveryLabel})`,
				`Cadence: ${cadenceLabel}`,
				completion,
			]
				.filter(Boolean)
				.join(' — ')
		})
		.join('\n')
}

function summarizeRecordRetention(data?: TrainingDocumentationFormValues) {
	const retention = data?.recordRetention
	if (!retention) return null
	return optionLabel(TRAINING_RECORD_RETENTION_OPTIONS, retention) ?? retention
}

const MONITORING_OWNER_SUMMARY: ReadonlyArray<{
	readonly key: keyof RegulatoryMonitoringFormValues['monitoringOwners']
	readonly label: string
}> = [
	{ key: 'federal', label: 'Federal rulemaking' },
	{ key: 'state', label: 'State legislation' },
	{ key: 'international', label: 'International developments' },
	{ key: 'retailer', label: 'Retailer requirements' },
	{ key: 'technical', label: 'Technical standards' },
	{ key: 'other', label: 'Other focus area' },
]

function summarizeMonitoringOwners(data?: RegulatoryMonitoringFormValues) {
	const owners = data?.monitoringOwners
	if (!owners) return null
	const lines: string[] = []
	for (const stage of MONITORING_OWNER_SUMMARY) {
		const contacts = owners[stage.key]
		if (!contacts || contacts.length === 0) continue
		const contactLines = contacts
			.map(
				(contact) =>
					contactReferenceLine(contact, `${stage.key}-${contact.contactId}`) ??
					contact.fallbackName ??
					contact.contactId,
			)
			.filter(Boolean)
		if (contactLines.length === 0) continue
		lines.push(`${stage.label} — ${contactLines.join(', ')}`)
	}
	return lines.length > 0 ? lines.join('\n') : null
}

function summarizeMonitoringFeeds(data?: RegulatoryMonitoringFormValues) {
	const feeds = data?.monitoringFeeds
	if (!feeds?.length) return null
	return feeds
		.map((feed) => {
			const sourceLabel =
				optionLabel(REGULATORY_FEED_OPTIONS, feed.source) ?? feed.source
			const frequencyLabel =
				optionLabel(REGULATORY_FEED_FREQUENCY_OPTIONS, feed.frequency) ??
				feed.frequency
			return [
				sourceLabel,
				`Frequency: ${frequencyLabel}`,
				normalizeValue(feed.detail),
			]
				.filter(Boolean)
				.join(' — ')
		})
		.join('\n')
}

const CHANGE_CONTROL_STAGE_SUMMARY: ReadonlyArray<{
	readonly key: keyof RegulatoryMonitoringFormValues['changeControl']
	readonly label: string
}> = [
	{ key: 'triage', label: 'Review / triage' },
	{ key: 'approval', label: 'Approval decision' },
	{ key: 'implementation', label: 'Implementation coordination' },
	{ key: 'communications', label: 'Communications / training' },
	{ key: 'verification', label: 'Verification / close-out' },
]

function summarizeChangeControlApprovers(
	data?: RegulatoryMonitoringFormValues,
) {
	const changeControl = data?.changeControl
	if (!changeControl) return null
	const lines: string[] = []
	for (const stage of CHANGE_CONTROL_STAGE_SUMMARY) {
		const contacts = changeControl[stage.key]
		if (!contacts || contacts.length === 0) continue
		const contactLines = contacts
			.map(
				(contact) =>
					contactReferenceLine(contact, `${stage.key}-${contact.contactId}`) ??
					contact.fallbackName ??
					contact.contactId,
			)
			.filter(Boolean)
		if (contactLines.length === 0) continue
		lines.push(`${stage.label} — ${contactLines.join(', ')}`)
	}
	return lines.length > 0 ? lines.join('\n') : null
}

function summarizeReviewCadence(data?: RegulatoryMonitoringFormValues) {
	const cadence = data?.reviewCadence
	if (!cadence) return null
	return optionLabel(REGULATORY_REVIEW_CADENCE_OPTIONS, cadence) ?? cadence
}

function summarizeImplementationWindow(data?: RegulatoryMonitoringFormValues) {
	const window = data?.implementationWindow
	if (!window) return null
	return optionLabel(IMPLEMENTATION_WINDOW_OPTIONS, window) ?? window
}

function summarizeTrackingTool(data?: RegulatoryMonitoringFormValues) {
	const tool = data?.trackingTool
	if (!tool) return null
	return optionLabel(IMPLEMENTATION_TRACKING_OPTIONS, tool) ?? tool
}

type PolicyReferenceEntry = PoliciesReferencesFormValues['documents'][number]
type PolicyReferenceType = PolicyReferenceEntry['type']

type PolicyReferenceStatus =
	(typeof POLICY_REFERENCE_STATUS_OPTIONS)[number]['value']

function getPolicyDocument(
	data: PoliciesReferencesFormValues | undefined,
	type: PolicyReferenceType,
) {
	return data?.documents?.find((document) => document.type === type)
}

function radioToBoolean(value: string | undefined): boolean | undefined {
	if (value === 'true') return true
	if (value === 'false') return false
	return undefined
}

function derivePolicyStatus(
	document?: PolicyReferenceEntry,
): PolicyReferenceStatus {
	if (!document) return 'in_place'
	const isComplete = radioToBoolean(document.isComplete)
	const hasDraft = radioToBoolean(document.hasDraft)
	const needsHelp = radioToBoolean(document.needsHelp)
	if (isComplete) return 'in_place'
	if (hasDraft) return 'in_development'
	if (needsHelp) return 'needs_template'
	return 'in_development'
}

function policyDocumentSummary(
	data: PoliciesReferencesFormValues | undefined,
	type: PolicyReferenceType,
) {
	const document = getPolicyDocument(data, type)
	const meta = POLICY_REFERENCE_ITEMS.find((item) => item.value === type)
	const title = meta?.label ?? 'Document reference'
	if (!document) return title
	const location = normalizeValue(document.location)
	const status = derivePolicyStatus(document)
	const statusLabel =
		optionLabel(POLICY_REFERENCE_STATUS_OPTIONS, status) ?? status
	const details = [location, statusLabel].filter(Boolean).join(' | ')
	return details ? `${title} — ${details}` : title
}

function contactLine(name?: string, email?: string, phone?: string) {
	const namePart = normalizeValue(name)
	const contactBits = [normalizeValue(email), normalizeValue(phone)].filter(
		Boolean,
	)
	const contactPart = contactBits.join(' · ')
	if (!namePart && !contactPart) return null
	if (namePart && contactPart) return `${namePart} — ${contactPart}`
	return namePart ?? contactPart
}

function contactReferenceLine(
	reference:
		| {
				contactId: string
				fallbackName?: string
				fallbackEmail?: string
				fallbackPhone?: string
		  }
		| undefined,
	defaultLabel: string,
) {
	if (!reference) return defaultLabel
	const name = reference.fallbackName?.trim().length
		? reference.fallbackName
		: defaultLabel
	return (
		contactLine(
			name ?? defaultLabel,
			reference.fallbackEmail,
			reference.fallbackPhone,
		) ?? defaultLabel
	)
}

function formatBrands(company?: CompanyFormValues) {
	return normalizeValue(company?.brandNames)
}

function formatAddress(address?: {
	street?: string
	street1?: string
	street2?: string
	city?: string
	state?: string
	region?: string
	postalCode?: string
	country?: string
}) {
	if (!address) return null
	const street = address.street ?? address.street1
	const city = address.city
	const stateValue = address.state ?? address.region
	const postalCode = address.postalCode
	const countryValue = address.country

	const stateLabel = stateValue
		? (optionLabel(US_STATE_OPTIONS, stateValue) ?? stateValue)
		: null
	const countryLabel = countryValue
		? (optionLabel(COUNTRY_OPTIONS, countryValue) ?? countryValue)
		: null

	const parts = [
		street,
		address.street2,
		city,
		stateLabel,
		postalCode,
		countryLabel,
	]
		.filter((part) => typeof part === 'string' && part.trim().length > 0)
		.map((part) => (part ?? '').trim())

	return parts.length ? parts.join(', ') : null
}

function resolvePrimaryContact(
	data: PrimaryContactsFormValues | undefined,
	key: keyof PrimaryContactsFormValues['assignments'],
) {
	if (!data) return null
	const contactId = data.assignments[key]
	if (!contactId) return null
	return data.contacts.find((contact) => contact.id === contactId) ?? null
}

function buildSummary(
	items: Array<{ label: string; value: string | null | undefined }>,
	generatedAt: string,
) {
	const rows = items.map(
		({ label, value }) => `> - ${label}: ${present(value ?? null)}`,
	)
	return rows.length
		? [`> **Auto-generated context — ${generatedAt}**`, ...rows]
		: []
}

export function renderTemplates(
	draft: IntakeDraft,
	generatedAt: string,
): RenderedTemplate[] {
	const helpers: TemplateHelpers = {
		present,
		optionLabel: (options, value) => optionLabel(options, value),
		buildSummary: (items) => buildSummary(items, generatedAt),
		contactLine,
	}
	return TEMPLATES.map((template) => {
		const summaryLines = template.summary(draft, helpers)
		const content = summaryLines.length
			? `${summaryLines.join('\n')}\n\n${template.markdown}`
			: template.markdown
		return { ...template, content, summaryLines }
	})
}
